import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import ProductCard from "../components/ProductCard";
import Spinner from "../components/Spinner";
import { useCart } from "../context/CartContext";
import { useSnackbar } from "notistack";
import ReactGA from "react-ga4";
import { getDiscountTiers, logAccess } from "../services/api";
import FullscreenImageViewer from "../components/FullscreenImageViewer";
import { useLanguage } from "../context/LanguageContext";
import { isImpersonating, getImpersonationData } from "../utils/impersonation";

function Shop() {
  const { t } = useLanguage();
  const isSimulating = isImpersonating();
  const token = localStorage.getItem("token");
  const tokenAcesso = localStorage.getItem("tokenAcesso");
  const isValid = (t) => {
    if (!t) return false;
    const clean = t.replace(/^["']|["']$/g, "").trim();
    return clean !== "" && clean !== "undefined" && clean !== "null";
  };
  const isLogged = isValid(token) || isValid(tokenAcesso);
  const user = JSON.parse(localStorage.getItem("user"));
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    if (isImpersonating()) {
      setIsAdmin(false);
      return;
    }
    let t = localStorage.getItem("token");
    if (t) {
      t = t.replace(/^["']|["']$/g, "").trim();
      if (t !== "" && t !== "undefined" && t !== "null") {
        setIsAdmin(true);
        return;
      }
    }
    setIsAdmin(false);
  }, []);
  const navigate = useNavigate();
  const { cartItems, addToCart, removeFromCart } = useCart();
  const { enqueueSnackbar } = useSnackbar();

  const [product, setProduct] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [temProdutos, setTemProdutos] = useState(false);
  const [evento, setEvento] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedAtletaFilter, setSelectedAtletaFilter] = useState("");
  const [tiers, setTiers] = useState([]);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [fullscreenIndex, setFullscreenIndex] = useState(null);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 250) {
        setShowScrollTop(true);
      } else {
        setShowScrollTop(false);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };



  const getProductTimestamp = (item) => {
    if (!item) return 0;
    if (item.createdAt) {
      const t = new Date(item.createdAt).getTime();
      if (!isNaN(t) && t > 0) return t;
    }
    if (item.dataCriacao) {
      const t = new Date(item.dataCriacao).getTime();
      if (!isNaN(t) && t > 0) return t;
    }
    // ObjectId do MongoDB: os primeiros 8 caracteres hexadecimais correspondem aos segundos da criação
    if (item._id && typeof item._id === "string" && item._id.length >= 8) {
      const seconds = parseInt(item._id.substring(0, 8), 16);
      if (!isNaN(seconds) && seconds > 0) return seconds * 1000;
    }
    return 0;
  };

  const compareMaisRecentes = (a, b) => {
    const timeA = getProductTimestamp(a);
    const timeB = getProductTimestamp(b);
    if (timeA !== timeB) {
      return timeB - timeA; // Imagens mais recentes primeiro
    }
    if (a?._id && b?._id) {
      return String(b._id).localeCompare(String(a._id));
    }
    return 0;
  };

  const totalQty = cartItems.reduce(
    (acc, item) => acc + (item.quantity || 0),
    0,
  );
  const totalPrice = cartItems.reduce(
    (acc, item) => acc + item.priceInCents * item.quantity,
    0,
  );

  const appliedPercent = (() => {
    if (!Array.isArray(tiers) || tiers.length === 0) return 0;
    const applicable = tiers.filter((t) => totalQty >= t.minQty);
    if (applicable.length === 0) return 0;
    return applicable[applicable.length - 1].percent;
  })();

  const maxPercent = (() => {
    if (!Array.isArray(tiers) || tiers.length === 0) return 0;
    return tiers.reduce((acc, t) => Math.max(acc, t.percent || 0), 0);
  })();

  const discountAmountInCents = Math.round((totalPrice * appliedPercent) / 100);
  const finalTotalInCents = totalPrice - discountAmountInCents;

  useEffect(() => {
    if (localStorage.getItem("adminImpersonation")) return;
    ReactGA.send({
      hitType: "pageview",
      page: window.location.pathname,
      title: "acessou_loja",
    });
    // Registrar acesso à área da loja (após login, nome será incluído pelo backend)
    try {
      logAccess("shop", "Shop");
    } catch (_) {}
  }, []);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        let token =
          localStorage.getItem("token") || localStorage.getItem("tokenAcesso");

        // Robust cleanup of token (remove quotes, spaces)
        if (token) {
          token = token.replace(/^["']|["']$/g, "").trim();
          if (token === "undefined" || token === "null") token = null;
        }

        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        if (isImpersonating()) {
          const imp = getImpersonationData();
          if (imp?.athleteId) {
            headers["x-impersonate-athlete-id"] = imp.athleteId;
          }
        }

        const response = await axios.get(
          `${import.meta.env.VITE_REACT_APP_BACKEND_BASEURL}/product`,
          { headers },
        );

        let all = response.data?.data || [];

        // Quando não houver usuário logado: mostrar todas as fotos da loja exceto brindes
        if (!isLogged) {
          all = all.filter(
            (p) =>
              !p.tags?.toLowerCase().includes("brinde") &&
              !p.tags?.toLowerCase().includes("oculta")
          );
        } else if (!isAdmin || isImpersonating()) {
          // Salvaguarda no front: atleta/simulado só deve visualizar fotos dele ou de seus representados
          const allowedIds = [];
          const allowedNames = [];
          if (user) {
            if (user.id || user._id) allowedIds.push(String(user.id || user._id));
            if (user.nome) allowedNames.push(user.nome.trim().toLowerCase());
            if (Array.isArray(user.atletasRepresentados)) {
              user.atletasRepresentados.forEach((rep) => {
                if (rep) {
                  const rId = rep._id || rep.id || (typeof rep === "string" ? rep : null);
                  if (rId) allowedIds.push(String(rId));
                  if (rep.nome) allowedNames.push(rep.nome.trim().toLowerCase());
                }
              });
            }
          }

          if (allowedIds.length > 0 || allowedNames.length > 0) {
            all = all.filter((p) => {
              const pAtletaId = p.atletaId ? String(p.atletaId) : null;
              if (pAtletaId && allowedIds.includes(pAtletaId)) return true;

              if (
                Array.isArray(p.atletasIds) &&
                p.atletasIds.some((id) => allowedIds.includes(String(id)))
              ) {
                return true;
              }

              if (
                Array.isArray(p.atletas) &&
                p.atletas.some((a) =>
                  allowedIds.includes(String(a.atletaId || a._id))
                )
              ) {
                return true;
              }

              const pAtletaNome = p.atletaNome ? p.atletaNome.toLowerCase() : "";
              if (
                pAtletaNome &&
                allowedNames.some((name) => pAtletaNome.includes(name))
              ) {
                return true;
              }

              if (
                Array.isArray(p.atletas) &&
                p.atletas.some((a) => {
                  const aNome = a.atletaNome ? a.atletaNome.toLowerCase() : "";
                  return aNome && allowedNames.some((name) => aNome.includes(name));
                })
              ) {
                return true;
              }

              return false;
            });
          }
        }

        const sortedProducts = all.sort(compareMaisRecentes);

        setProduct(sortedProducts);
        setFilteredProducts(sortedProducts);
        setTemProdutos(sortedProducts.length > 0);
      } catch (error) {
        console.error("Error fetching products:", error);
        if (error.response?.status === 401 && isLogged) {
          localStorage.removeItem("token");
          localStorage.removeItem("tokenAcesso");
          localStorage.removeItem("user");
          enqueueSnackbar("Sessão expirada. Acesse novamente.", {
            variant: "warning",
          });
          navigate("/acesso");
          return;
        }
        enqueueSnackbar("Problemas ao buscar produtos", { variant: "error" });
      } finally {
        setLoading(false);
      }
    };

    const fetchTiers = async () => {
      try {
        const t = await getDiscountTiers();
        // Em Shop usamos silenciosamente, pois a rota pode exigir auth
        const data = t?.data ?? t;
        if (Array.isArray(data)) {
          setTiers(data);
        } else if (data && typeof data === "object") {
          setTiers(Array.isArray(data.tiers) ? data.tiers : []);
        } else {
          setTiers([]);
        }
      } catch (error) {
        setTiers([]);
      }
    };

    // Add a small delay to ensure localStorage is ready
    setTimeout(() => {
      fetchProducts();
      fetchTiers();
    }, 100);

    // eslint-disable-next-line
  }, [user?.id, user?.perfil, isAdmin]);

  const filterProducts = () => {
    if (!Array.isArray(product)) {
      console.error("Products is not an array:", product);
      return;
    }

    let filtered = [...product];

    // Filtro por atleta selecionado
    if (selectedAtletaFilter !== "") {
      const selectedRep = Array.isArray(user?.atletasRepresentados)
        ? user.atletasRepresentados.find(
            (a) => (a._id || a) === selectedAtletaFilter
          )
        : null;
      const targetName =
        selectedAtletaFilter === user?.id ? user?.nome : selectedRep?.nome;

      filtered = filtered.filter(
        (p) =>
          p.atletaId === selectedAtletaFilter ||
          (Array.isArray(p.atletasIds) &&
            p.atletasIds.includes(selectedAtletaFilter)) ||
          (Array.isArray(p.atletas) &&
            p.atletas.some(
              (a) => (a.atletaId || a._id) === selectedAtletaFilter
            )) ||
          (targetName &&
            p.atletaNome?.toLowerCase().includes(targetName.toLowerCase()))
      );
    }

    // Filtro por evento
    if (evento !== "") {
      filtered = filtered.filter((product) => product.evento === evento);
    }

    // Filtro por termos de pesquisa
    if (searchTerm !== "") {
      const terms = searchTerm
        .toLowerCase()
        .split(" ")
        .filter((term) => term !== "");
      filtered = filtered.filter((product) =>
        terms.every(
          (term) =>
            product.evento.toLowerCase().includes(term) ||
            product.tags?.toLowerCase().includes(term) ||
            product.local.toLowerCase().includes(term) ||
            product.atletaNome?.toLowerCase().includes(term) ||
            (Array.isArray(product.atletas) &&
              product.atletas.some((a) =>
                a.atletaNome?.toLowerCase().includes(term)
              )),
        ),
      );
    }

    filtered.sort(compareMaisRecentes);
    setFilteredProducts(filtered);
  };

  useEffect(() => {
    filterProducts();
    // eslint-disable-next-line
  }, [evento, searchTerm, selectedAtletaFilter]);

  return (
    <div className={`p-4 ${isLogged ? (isSimulating ? "mt-48" : "mt-40") : "mt-24"}`}>
      <div className="flex flex-col">
        {isLogged && tiers && tiers.length > 0 ? (
          <div className="badge badge-info gap-1 m-4">
            {appliedPercent > 0
              ? t("shop.discountApplied", { percent: appliedPercent })
              : t("shop.upToDiscount", { percent: maxPercent })}
          </div>
        ) : null}
        <div className="text-xl font-semibold mb-2">
          {isLogged
            ? t("shop.greeting", { name: user?.nome || t("shop.defaultAthleteName") })
            : t("shop.photoGallery")}
        </div>
        <div className="flex flex-row justify-between items-center">
        </div>

        {/* Filtro rápido por atletas representados */}
        {Array.isArray(user?.atletasRepresentados) &&
          user.atletasRepresentados.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-3 items-center">
              <span className="text-xs opacity-75 font-semibold mr-1">
                {t("common.photos")}:
              </span>
              <button
                type="button"
                onClick={() => setSelectedAtletaFilter("")}
                className={`btn btn-xs ${
                  selectedAtletaFilter === ""
                    ? "btn-primary"
                    : "btn-outline opacity-70"
                }`}
              >
                {t("common.all")}
              </button>
              {user.id && user.nome && (
                <button
                  type="button"
                  onClick={() =>
                    setSelectedAtletaFilter(
                      selectedAtletaFilter === user.id ? "" : user.id
                    )
                  }
                  className={`btn btn-xs ${
                    selectedAtletaFilter === user.id
                      ? "btn-primary"
                      : "btn-outline opacity-70"
                  }`}
                >
                  {user.nome} ({t("common.you")})
                </button>
              )}
              {user.atletasRepresentados.map((a) => {
                const aId = a._id || a;
                const aNome = a.nome || "Atleta";
                return (
                  <button
                    key={aId}
                    type="button"
                    onClick={() =>
                      setSelectedAtletaFilter(
                        selectedAtletaFilter === aId ? "" : aId
                      )
                    }
                    className={`btn btn-xs ${
                      selectedAtletaFilter === aId
                        ? "btn-primary"
                        : "btn-outline opacity-70"
                    }`}
                  >
                    {aNome}
                  </button>
                );
              })}
            </div>
          )}

        <div className="flex flex-col mb-4">
          <span className="text-sm opacity-70 mx-2 mb-1">
            {t("shop.searchLabel")}
          </span>
          <div className="relative w-full">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input input-bordered w-full pr-10"
              placeholder={t("shop.searchPlaceholder")}
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm("")}
                title={t("shop.clearSearch")}
                aria-label={t("shop.clearSearch")}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-base-content/50 hover:text-base-content hover:bg-base-200 rounded-full transition-colors"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            )}
          </div>
        </div>

        {isLogged && temProdutos ? (
          <div className={`fixed ${isSimulating ? "top-[118px]" : "top-[74px]"} left-4 z-10 transition-all`}>
            <button
              onClick={() => {
                navigate("/ar/cart");
              }}
              className="bg-blue-500 hover:bg-blue-800 text-white py-2 px-4 rounded-md"
            >
              <div className="flex flex-col text-left">
                <div className="flex justify-between gap-4">
                  <span>{t("common.price")}:</span>
                  <span>
                    R$
                    {(totalPrice / 100).toLocaleString("pt-BR", {
                      minimumFractionDigits: 2,
                    })}
                  </span>
                </div>
                <div className="flex justify-between gap-4">
                  <span>
                    {t("shop.discountTag")} {appliedPercent ? `(${appliedPercent}%)` : ""}:
                  </span>
                  <span>
                    - R$
                    {(discountAmountInCents / 100).toLocaleString("pt-BR", {
                      minimumFractionDigits: 2,
                    })}
                  </span>
                </div>
                <div className="flex justify-between gap-4 font-semibold">
                  <span>{t("common.total")}:</span>
                  <span>
                    R$
                    {(finalTotalInCents / 100).toLocaleString("pt-BR", {
                      minimumFractionDigits: 2,
                    })}
                  </span>
                </div>
              </div>
            </button>
          </div>
        ) : null}
      </div>

      {filteredProducts.length === 0 ? (
        <div className="text-center text-lg mt-4">{t("shop.noPhotosFound")}</div>
      ) : (
        <>
          {loading && <Spinner />}
          <ProductCard
            product={filteredProducts}
            isPublicView={!isLogged}
            onSelectImage={(_item, idx) => setFullscreenIndex(idx)}
          />
        </>
      )}

      {/* Botão flutuante para voltar ao topo da página (canto inferior esquerdo) */}
      {showScrollTop && (
        <div className="fixed bottom-6 left-6 z-50 transition-all duration-300">
          <button
            type="button"
            onClick={scrollToTop}
            title={t("common.backToTop")}
            aria-label={t("common.backToTop")}
            id="btn-voltar-ao-topo"
            className="btn btn-circle bg-blue-500 hover:bg-blue-600 active:bg-blue-700 shadow-2xl text-white hover:scale-110 active:scale-95 transition-all duration-200 border-none flex items-center justify-center group"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 group-hover:-translate-y-0.5 transition-transform duration-200"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2.5}
                d="M5 15l7-7 7 7"
              />
            </svg>
          </button>
        </div>
      )}

      {/* Botão flutuante para comprar fotos na visão pública */}
      {!isLogged && (
        <div className="fixed bottom-6 right-6 z-50">
          <button
            onClick={() => navigate("/acesso")}
            className="btn bg-blue-500 hover:bg-blue-600 active:bg-blue-700 btn-lg shadow-2xl gap-2 font-bold text-white uppercase tracking-wider hover:scale-105 transition-all duration-200 border-none"
            id="btn-comprar-fotos"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
              />
            </svg>
            {t("shop.buyPhotos")}
          </button>
        </div>
      )}

      {/* Visualizador de Foto em Tela Cheia */}
      <FullscreenImageViewer
        products={filteredProducts}
        currentIndex={fullscreenIndex}
        onClose={() => setFullscreenIndex(null)}
        onChangeIndex={(idx) => setFullscreenIndex(idx)}
        isLogged={isLogged}
        isHome={false}
        cartItems={cartItems}
        addToCart={addToCart}
        removeFromCart={removeFromCart}
      />
    </div>
  );
}

export default Shop;
